/* ============================================================
   Image Carousel / Gallery Section — behavior
   - Detects whether the row actually overflows (more than fits
     on screen) and only then shows the glass arrow buttons /
     lets the desktop row scroll instead of center.
   - Arrows scroll by one "page" (visible width) with native
     smooth scrolling; they hide themselves at each end.
   - Lightbox: click an image -> full-size viewer with every
     image as a thumbnail underneath. Thumbnails, arrow keys,
     glass arrows and swipe all navigate between images.
   - Re-initializes on shopify:section:load for the theme editor.
   ============================================================ */

function initAllImageCarousels(root) {
  var scope = root || document;
  scope.querySelectorAll('[data-carousel-section]').forEach(function (section) {
    if (section.dataset.carouselInit === 'true') return;
    section.dataset.carouselInit = 'true';
    initImageCarousel(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    initAllImageCarousels();
  });
} else {
  initAllImageCarousels();
}

document.addEventListener('shopify:section:load', function (event) {
  initAllImageCarousels(event.target);
});

function initImageCarousel(section) {
  var track = section.querySelector('[data-carousel-track]');
  var list = section.querySelector('[data-carousel-list]');
  var prevBtn = section.querySelector('[data-carousel-prev]');
  var nextBtn = section.querySelector('[data-carousel-next]');
  var items = Array.prototype.slice.call(section.querySelectorAll('[data-carousel-item]'));
  if (!track || !list || !items.length) return;

  function isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function updateOverflowState() {
    var overflowing = list.scrollWidth > track.clientWidth + 2;
    if (overflowing || isMobile()) {
      section.setAttribute('data-scrollable-desktop', '');
    } else {
      section.removeAttribute('data-scrollable-desktop');
    }
    updateArrowVisibility();
  }

  function updateArrowVisibility() {
    var canScroll = list.scrollWidth > track.clientWidth + 2;
    if (!canScroll) {
      if (prevBtn) prevBtn.hidden = true;
      if (nextBtn) nextBtn.hidden = true;
      return;
    }
    var atStart = track.scrollLeft <= 4;
    var atEnd = track.scrollLeft + track.clientWidth >= list.scrollWidth - 4;
    if (prevBtn) prevBtn.hidden = atStart;
    if (nextBtn) nextBtn.hidden = atEnd;
  }

  function scrollByPage(direction) {
    var amount = track.clientWidth * 0.9 * direction;
    track.scrollBy({ left: amount, behavior: 'smooth' });
  }

  if (prevBtn) prevBtn.addEventListener('click', function () { scrollByPage(-1); });
  if (nextBtn) nextBtn.addEventListener('click', function () { scrollByPage(1); });

  track.addEventListener('scroll', debounce(updateArrowVisibility, 50));
  window.addEventListener('resize', debounce(updateOverflowState, 150));

  // Re-check once images have loaded (widths/heights can shift layout)
  var imgs = section.querySelectorAll('img.image-carousel-img');
  var pending = imgs.length;
  if (pending === 0) {
    updateOverflowState();
  } else {
    imgs.forEach(function (img) {
      if (img.complete) {
        pending--;
      } else {
        img.addEventListener('load', function () {
          pending--;
          if (pending <= 0) updateOverflowState();
        });
        img.addEventListener('error', function () {
          pending--;
        });
      }
    });
    updateOverflowState();
  }

  /* ---------------- Lightbox ---------------- */
  var lightboxEnabled = section.dataset.lightboxEnabled === 'true';
  if (!lightboxEnabled) return;

  var lightbox = document.querySelector('[data-carousel-lightbox]');
  if (!lightbox) return;

  var lightboxImg = lightbox.querySelector('[data-carousel-lightbox-img]');
  var thumbsWrap = lightbox.querySelector('[data-carousel-lightbox-thumbs]');
  var lbPrev = lightbox.querySelector('[data-carousel-lightbox-prev]');
  var lbNext = lightbox.querySelector('[data-carousel-lightbox-next]');
  var closeEls = lightbox.querySelectorAll('[data-carousel-lightbox-close]');

  var slides = items.map(function (item) {
    var img = item.querySelector('.image-carousel-img');
    return {
      full: img ? (img.getAttribute('data-full-src') || img.src) : '',
      alt: img ? img.alt : ''
    };
  });

  var currentIndex = 0;
  var thumbEls = [];
  var lastFocused = null;

  function buildThumbs() {
    if (thumbsWrap.dataset.built === 'true') return;
    thumbsWrap.dataset.built = 'true';
    slides.forEach(function (slide, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'image-carousel-lightbox-thumb';
      btn.setAttribute('data-index', i);
      btn.setAttribute('aria-label', 'Show image ' + (i + 1));
      var img = document.createElement('img');
      img.src = slide.full;
      img.alt = slide.alt || '';
      img.loading = 'lazy';
      btn.appendChild(img);
      btn.addEventListener('click', function () {
        showSlide(i);
      });
      thumbsWrap.appendChild(btn);
      thumbEls.push(btn);
    });
  }

  function showSlide(index) {
    if (!slides.length) return;
    currentIndex = (index + slides.length) % slides.length;
    var slide = slides[currentIndex];

    lightboxImg.classList.remove('is-visible');
    window.setTimeout(function () {
      lightboxImg.src = slide.full;
      lightboxImg.alt = slide.alt || '';
      requestAnimationFrame(function () {
        lightboxImg.classList.add('is-visible');
      });
    }, 90);

    thumbEls.forEach(function (thumb, i) {
      thumb.classList.toggle('is-active', i === currentIndex);
      if (i === currentIndex) {
        thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    });
  }

  function openLightbox(index) {
    buildThumbs();
    lastFocused = document.activeElement;
    lightbox.classList.add('is-open');
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.classList.add('image-carousel-lightbox-locked');
    showSlide(index);
    window.setTimeout(function () {
      closeEls[0] && closeEls[0].focus();
    }, 50);
    document.addEventListener('keydown', onKeydown);
  }

  function closeLightbox() {
    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('image-carousel-lightbox-locked');
    document.removeEventListener('keydown', onKeydown);
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') showSlide(currentIndex - 1);
    if (e.key === 'ArrowRight') showSlide(currentIndex + 1);
  }

  section.querySelectorAll('[data-carousel-open]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var index = parseInt(btn.getAttribute('data-index'), 10) || 0;
      openLightbox(index);
    });
  });

  closeEls.forEach(function (el) {
    el.addEventListener('click', closeLightbox);
  });

  if (lbPrev) lbPrev.addEventListener('click', function () { showSlide(currentIndex - 1); });
  if (lbNext) lbNext.addEventListener('click', function () { showSlide(currentIndex + 1); });

  // Basic swipe support on the main stage for touch devices
  var stage = lightbox.querySelector('.image-carousel-lightbox-stage');
  var touchStartX = null;
  if (stage) {
    stage.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    stage.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var deltaX = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(deltaX) > 40) {
        showSlide(currentIndex + (deltaX < 0 ? 1 : -1));
      }
      touchStartX = null;
    });
  }
}

function debounce(fn, wait) {
  var t;
  return function () {
    var args = arguments;
    var ctx = this;
    clearTimeout(t);
    t = setTimeout(function () {
      fn.apply(ctx, args);
    }, wait);
  };
}